/**
 * Stock chart component for displaying stock price history
 * Features:
 * - Interactive crosshair on hover with tooltip
 * - Period performance indicator with colored background
 * - Responsive SVG chart
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import type { StockData, StockHistory } from '../../types';

interface StockChartProps {
  data: StockData;
  isLoading?: boolean;
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  /** Market cap in dollars for P/S calculation */
  marketCap?: number;
  /** Revenue in dollars for P/S calculation */
  revenue?: number;
}

const PERIODS = [
  { label: '1W', value: '5d' },
  { label: '1M', value: '1mo' },
  { label: '3M', value: '3mo' },
  { label: '6M', value: '6mo' },
  { label: '1Y', value: '1y' },
];

function formatPrice(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatChange(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Period performance badge component
function PeriodPerformanceBadge({
  change,
  periodLabel
}: {
  change: number | null | undefined;
  periodLabel: string;
}) {
  if (change === null || change === undefined) return null;

  const isPositive = change >= 0;
  const arrow = isPositive ? '↑' : '↓';

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium ${
        isPositive
          ? 'bg-green-100 text-green-800'
          : 'bg-rose-100 text-rose-800'
      }`}
    >
      {arrow}{Math.abs(change).toFixed(2)}%
      <span className="ml-1.5 text-xs opacity-75">{periodLabel}</span>
    </span>
  );
}

// Calculate rolling moving average
function calculateMA(data: number[], period: number): (number | null)[] {
  const result: (number | null)[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push(null);
    } else {
      const slice = data.slice(i - period + 1, i + 1);
      const sum = slice.reduce((acc, val) => acc + val, 0);
      result.push(sum / period);
    }
  }
  return result;
}

// Interactive line chart with crosshair
function InteractiveLineChart({
  history,
  width = 600,
  height = 200,
  showMA = true,
}: {
  history: StockHistory;
  width?: number;
  height?: number;
  showMA?: boolean;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const data = history.close;
  const dates = history.dates;

  // PERF: Only calculate MA when showMA is true and memoize to avoid recalc on re-render
  const ma30Data = useMemo(() => {
    if (!showMA) return [];
    return calculateMA(data, 30);
  }, [data, showMA]);

  const padding = { top: 20, right: 20, bottom: 30, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || !data.length) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - padding.left;

    // Calculate which data point we're hovering over
    const dataIndex = Math.round((x / chartWidth) * (data.length - 1));
    const clampedIndex = Math.max(0, Math.min(data.length - 1, dataIndex));

    setHoverIndex(clampedIndex);
  }, [data.length, chartWidth, padding.left]);

  const handleMouseLeave = useCallback(() => {
    setHoverIndex(null);
  }, []);

  if (!data.length) return null;

  // Include MA values in min/max calculation for proper scaling
  const validMAValues = ma30Data.filter((v): v is number => v !== null);
  const allValues = showMA && validMAValues.length > 0
    ? [...data, ...validMAValues]
    : data;
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  // Calculate point positions
  const getX = (index: number) => padding.left + (index / (data.length - 1)) * chartWidth;
  const getY = (value: number) => padding.top + chartHeight - ((value - minValue) / valueRange) * chartHeight;

  // Generate path points
  const points = data.map((value, index) => `${getX(index)},${getY(value)}`);
  const pathD = `M ${points.join(' L ')}`;

  // Generate gradient fill
  const areaPath = `${pathD} L ${padding.left + chartWidth},${padding.top + chartHeight} L ${padding.left},${padding.top + chartHeight} Z`;

  // Generate MA path (only for valid values)
  const maPathSegments: string[] = [];
  let maPathStarted = false;
  ma30Data.forEach((value, index) => {
    if (value !== null) {
      const point = `${getX(index)},${getY(value)}`;
      if (!maPathStarted) {
        maPathSegments.push(`M ${point}`);
        maPathStarted = true;
      } else {
        maPathSegments.push(`L ${point}`);
      }
    }
  });
  const maPathD = maPathSegments.join(' ');

  // Y-axis labels
  const yLabels = [maxValue, (maxValue + minValue) / 2, minValue].map((value, i) => ({
    value: formatPrice(value),
    y: padding.top + (i * chartHeight) / 2,
  }));

  // Determine line color based on trend
  const lastValue = data[data.length - 1] ?? 0;
  const firstValue = data[0] ?? 0;
  const isPositive = lastValue >= firstValue;
  const lineColor = isPositive ? '#10B981' : '#EF4444';
  const fillColor = isPositive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';

  // Hover point data
  const hoverValue = hoverIndex !== null ? (data[hoverIndex] ?? null) : null;
  const hoverMAValue = hoverIndex !== null ? (ma30Data[hoverIndex] ?? null) : null;
  const hoverDate = hoverIndex !== null ? (dates[hoverIndex] ?? null) : null;
  const hoverX = hoverIndex !== null ? getX(hoverIndex) : 0;
  const hoverY = hoverValue !== null ? getY(hoverValue) : 0;
  const hoverMAY = hoverMAValue !== null && hoverMAValue !== undefined ? getY(hoverMAValue) : 0;

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="overflow-visible cursor-crosshair"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Grid lines */}
      <g className="text-gray-200">
        {[0, 1, 2].map((i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + (i * chartHeight) / 2}
            x2={padding.left + chartWidth}
            y2={padding.top + (i * chartHeight) / 2}
            stroke="currentColor"
            strokeDasharray="4"
          />
        ))}
      </g>

      {/* Y-axis labels */}
      <g className="text-xs fill-gray-500">
        {yLabels.map((label, i) => (
          <text key={i} x={padding.left - 8} y={label.y + 4} textAnchor="end">
            {label.value}
          </text>
        ))}
      </g>

      {/* Area fill */}
      <path d={areaPath} fill={fillColor} />

      {/* 30-day MA line */}
      {showMA && maPathD && (
        <path
          d={maPathD}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={2}
          strokeDasharray="6 3"
          opacity={0.8}
        />
      )}

      {/* Price line */}
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth={2} />

      {/* Current price dot (rightmost point) */}
      <circle
        cx={padding.left + chartWidth}
        cy={getY(lastValue)}
        r={4}
        fill={lineColor}
      />

      {/* Hover crosshair and tooltip */}
      {hoverIndex !== null && hoverValue !== null && (
        <>
          {/* Vertical dashed line */}
          <line
            x1={hoverX}
            y1={padding.top}
            x2={hoverX}
            y2={padding.top + chartHeight}
            stroke="#6B7280"
            strokeWidth={1}
            strokeDasharray="4 2"
          />

          {/* Hover point - Price */}
          <circle
            cx={hoverX}
            cy={hoverY}
            r={5}
            fill={lineColor}
            stroke="white"
            strokeWidth={2}
          />

          {/* Hover point - MA */}
          {showMA && hoverMAValue !== null && (
            <circle
              cx={hoverX}
              cy={hoverMAY}
              r={4}
              fill="#F59E0B"
              stroke="white"
              strokeWidth={2}
            />
          )}

          {/* Tooltip */}
          <g>
            {/* Tooltip background - taller if showing MA */}
            <rect
              x={hoverX - 50}
              y={hoverY - (showMA && hoverMAValue !== null ? 68 : 50)}
              width={100}
              height={showMA && hoverMAValue !== null ? 58 : 40}
              rx={4}
              fill="white"
              stroke="#E5E7EB"
              strokeWidth={1}
              filter="drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))"
            />
            {/* Price */}
            <text
              x={hoverX}
              y={hoverY - (showMA && hoverMAValue !== null ? 50 : 32)}
              textAnchor="middle"
              className="text-sm font-semibold fill-gray-900"
              style={{ fontSize: '14px', fontWeight: 600 }}
            >
              {formatPrice(hoverValue)}
            </text>
            {/* MA value */}
            {showMA && hoverMAValue !== null && (
              <text
                x={hoverX}
                y={hoverY - 34}
                textAnchor="middle"
                style={{ fontSize: '12px', fill: '#F59E0B' }}
              >
                MA: {formatPrice(hoverMAValue)}
              </text>
            )}
            {/* Date */}
            {hoverDate && (
              <text
                x={hoverX}
                y={hoverY - (showMA && hoverMAValue !== null ? 20 : 18)}
                textAnchor="middle"
                className="text-xs fill-gray-500"
                style={{ fontSize: '11px' }}
              >
                {formatDate(hoverDate)}
              </text>
            )}
          </g>
        </>
      )}
    </svg>
  );
}

export function StockChart({ data, isLoading, selectedPeriod, onPeriodChange, marketCap, revenue }: StockChartProps) {
  const [showMA, setShowMA] = useState(true);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="h-48 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  const { ticker, price_change, range_52_week, moving_averages, history } = data;
  const currentPrice = price_change.current_price;
  const weekChange = price_change.changes['1W'];
  const monthChange = price_change.changes['1M'];
  const ytdChange = price_change.changes['YTD'];

  // Get period change based on selected period
  const periodChangeMap: Record<string, { change: number | null | undefined; label: string }> = {
    '5d': { change: weekChange, label: '1W' },
    '1mo': { change: monthChange, label: '1M' },
    '3mo': { change: price_change.changes['3M'], label: '3M' },
    '6mo': { change: price_change.changes['6M'], label: '6M' },
    '1y': { change: price_change.changes['1Y'], label: '1Y' },
  };

  const periodInfo = periodChangeMap[selectedPeriod] || { change: monthChange, label: '1M' };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900">{ticker}</h3>
            <span className="text-2xl font-bold text-gray-900">
              {formatPrice(currentPrice)}
            </span>
            {/* Period performance badge */}
            <PeriodPerformanceBadge
              change={periodInfo.change}
              periodLabel={periodInfo.label}
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            As of {new Date(price_change.as_of_date).toLocaleString()}
          </p>
        </div>

        {/* Period selector */}
        <div className="flex gap-1">
          {PERIODS.map((period) => (
            <button
              key={period.value}
              onClick={() => onPeriodChange(period.value)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedPeriod === period.value
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="mb-4">
        <InteractiveLineChart
          history={history}
          width={600}
          height={200}
          showMA={showMA}
        />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-0.5"
            style={{ backgroundColor: (history.close[history.close.length - 1] ?? 0) >= (history.close[0] ?? 0) ? '#10B981' : '#EF4444' }}
          />
          <span className="text-gray-600">Price</span>
        </div>
        <button
          onClick={() => setShowMA(!showMA)}
          className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${
            showMA ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-400'
          }`}
        >
          <div
            className="w-4 h-0.5"
            style={{
              backgroundColor: '#F59E0B',
              backgroundImage: 'repeating-linear-gradient(90deg, #F59E0B 0, #F59E0B 4px, transparent 4px, transparent 6px)',
              opacity: showMA ? 1 : 0.4,
            }}
          />
          <span>30-Day MA</span>
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
        {/* 52-week range */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">52-Week Range</p>
          <p className="text-sm font-medium text-gray-900 mt-1">
            {formatPrice(range_52_week.low)} - {formatPrice(range_52_week.high)}
          </p>
          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 rounded-full"
              style={{ width: `${range_52_week.position_in_range}%` }}
            />
          </div>
        </div>

        {/* Moving averages */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">30-Day MA</p>
          <p className="text-sm font-medium text-gray-900 mt-1">
            {moving_averages.values[30] ? formatPrice(moving_averages.values[30]) : 'N/A'}
          </p>
          {moving_averages.values[30] && (
            <p
              className={`text-xs mt-1 ${
                currentPrice >= moving_averages.values[30] ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {currentPrice >= moving_averages.values[30] ? 'Above' : 'Below'} MA
            </p>
          )}
        </div>

        {/* Month change */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">1M Change</p>
          <p
            className={`text-sm font-medium mt-1 ${
              (monthChange ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatChange(monthChange)}
          </p>
        </div>

        {/* YTD change */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">YTD Return</p>
          <p
            className={`text-sm font-medium mt-1 ${
              (ytdChange ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatChange(ytdChange)}
          </p>
        </div>

        {/* Price-to-Sales */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">P/S Ratio</p>
          <p className="text-sm font-medium text-gray-900 mt-1">
            {marketCap && revenue && revenue > 0
              ? (marketCap / revenue).toFixed(2) + 'x'
              : 'N/A'}
          </p>
          {marketCap && revenue && revenue > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              MCap / Revenue
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default StockChart;
